import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { X, Check, Bell, User, Calendar, Clock } from 'lucide-react';
import { AuthContext } from '../App';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NotificationPanel = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const { tt } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const notificationsRef = useRef([]);

  const NotificationTypes = {
    CLIENT_ASSIGNED: 'client_assigned',
    REMINDER_UPCOMING: 'reminder_upcoming',
    REMINDER_DUE: 'reminder_due'
  };

  // Sync ref with state
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Load from localStorage
  useEffect(() => {
    if (!user) return;
    const storageKey = `notifications_${user.id}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
        notificationsRef.current = parsed;
      } catch (e) {}
    }
  }, [user]);

  const saveNotifications = (updated) => {
    if (!user) return;
    // Keep max 50 notifications, remove old ones
    const trimmed = updated.slice(0, 50);
    localStorage.setItem(`notifications_${user.id}`, JSON.stringify(trimmed));
    setNotifications(trimmed);
    notificationsRef.current = trimmed;
  };

  const addNotification = (notification) => {
    const current = notificationsRef.current;
    const newNotif = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };
    const updated = [newNotif, ...current];
    saveNotifications(updated);
    setIsVisible(true);
  };

  const markAsRead = (notificationId) => {
    const updated = notificationsRef.current.map(n =>
      n.id === notificationId ? { ...n, read: true } : n
    );
    saveNotifications(updated);
  };

  const markAllAsRead = () => {
    const updated = notificationsRef.current.map(n => ({ ...n, read: true }));
    saveNotifications(updated);
    setIsVisible(false);
  };

  const removeNotification = (notificationId) => {
    const updated = notificationsRef.current.filter(n => n.id !== notificationId);
    saveNotifications(updated);
    if (updated.length === 0) setIsVisible(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case NotificationTypes.CLIENT_ASSIGNED:
        return <User className="w-5 h-5 text-blue-500" />;
      case NotificationTypes.REMINDER_UPCOMING:
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case NotificationTypes.REMINDER_DUE:
        return <Calendar className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffMinutes < 1) return tt('сейчас');
    if (diffMinutes < 60) return `${diffMinutes} ${tt('мин. назад')}`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} ${tt('ч. назад')}`;
    return date.toLocaleDateString('ru-RU');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Register global API using ref-based addNotification (no stale closure!)
  useEffect(() => {
    window.NotificationPanel = {
      addClientAssignedNotification: (clientName, clientId) => {
        addNotification({
          type: 'client_assigned',
          title: tt('За вами закреплен новый клиент'),
          message: `${tt('Клиент')} "${clientName}" ${tt('был назначен вам')}`,
          clientId, clientName
        });
      },
      addReminderNotification: (reminderTitle, clientName, clientId, isUpcoming = true) => {
        addNotification({
          type: isUpcoming ? 'reminder_upcoming' : 'reminder_due',
          title: isUpcoming ? tt('Предстоящее напоминание') : tt('Напоминание'),
          message: `${reminderTitle} - ${clientName}`,
          clientId, clientName, reminderTitle
        });
      }
    };
  }, []);  // Empty deps - addNotification uses ref, so no stale closure

  if (!user) return null;

  return (
    <>
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={() => setIsVisible(!isVisible)}
          className="relative bg-white rounded-full p-3 shadow-lg border hover:shadow-xl transition-shadow"
          title={tt('Уведомления')}
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5 text-gray-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {isVisible && (
        <div className="fixed top-16 right-4 z-50 w-80 max-h-96 bg-white rounded-lg shadow-xl border overflow-hidden">
          <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
            <h3 className="font-semibold text-gray-800">
              {tt('Уведомления')} {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="text-xs text-blue-600 hover:text-blue-800" title={tt('Прочитать все')}>
                  <Check className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => setIsVisible(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                {tt('Нет уведомлений')}
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${!notif.read ? 'bg-blue-50' : ''}`}
                  onClick={() => {
                    markAsRead(notif.id);
                    if (notif.clientId) navigate(`/crm/clients/${notif.clientId}`);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{notif.title}</p>
                      <p className="text-xs text-gray-600 truncate">{notif.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatTime(notif.timestamp)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeNotification(notif.id); }}
                      className="flex-shrink-0 text-gray-300 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationPanel;
